import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ballColor, ballColorDark } from '@photo-merge/shared';
import { Button } from '../components/Button';
import { useGame } from './useGame';
import { ShareScore } from './ShareScore';
import type { BallArt } from './art';
import styles from './GameBoard.module.css';

interface GameBoardProps {
  ballCount: number;
  art: BallArt;
  /** Namespaces the personal-best entry in localStorage, per game. */
  bestKey: string;
  /** Fired when the board fills, so a saved game can offer the leaderboard. */
  onGameOver?: (score: number) => void;
  /** Extra content for the game-over panel — the score submit and leaderboard. */
  overlayExtra?: ReactNode;
  /** Rendered directly under the board, inside the same fixed-height column. */
  footer?: ReactNode;
  /** Called before a restart, so the parent can clear per-round state. */
  onRestart?: () => void;
  /** Where a shared score should send people. Omit to hide the share button. */
  shareUrl?: string;
  /** Named games get a mention in the shared message. */
  shareTitle?: string;
}

function readBest(key: string): number {
  const raw = localStorage.getItem(`pm.best.${key}`);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Encoding a 256px sprite to a data URL costs real time, and the snapshot updates
 * ~12x a second, so the result is cached against the sprite itself.
 */
const dataUrlCache = new WeakMap<object, string>();

function spriteUrl(image: CanvasImageSource | null): string | undefined {
  // A saved game's art is <img> elements loaded from storage, which already have a
  // usable URL. Only freshly rendered canvases need encoding.
  if (image instanceof HTMLImageElement) return image.src;
  if (!(image instanceof HTMLCanvasElement)) return undefined;
  let url = dataUrlCache.get(image);
  if (!url) {
    url = image.toDataURL();
    dataUrlCache.set(image, url);
  }
  return url;
}

/** Small circle preview of the next ball, matching the in-game ring treatment. */
function NextBall({ rank, count, art }: { rank: number; count: number; art: BallArt }) {
  const src = spriteUrl(art.imageFor(rank));
  const size = 34;
  return (
    <span
      className={styles.nextBall}
      style={{
        width: size,
        height: size,
        border: `4px solid ${ballColor(rank, count)}`,
        outline: `1px solid ${ballColorDark(rank, count)}`,
        backgroundImage: src ? `url(${src})` : undefined,
        backgroundColor: src ? undefined : ballColor(rank, count),
      }}
      aria-hidden="true"
    />
  );
}

export function GameBoard({
  ballCount,
  art,
  bestKey,
  onGameOver,
  overlayExtra,
  footer,
  onRestart,
  shareUrl,
  shareTitle,
}: GameBoardProps) {
  const [best, setBest] = useState(() => readBest(bestKey));
  const [beatBest, setBeatBest] = useState(false);
  const dangerRef = useRef<HTMLDivElement>(null);

  const handleGameOver = useCallback(
    (score: number) => {
      const previous = readBest(bestKey);
      if (score > previous) {
        localStorage.setItem(`pm.best.${bestKey}`, String(score));
        setBest(score);
        setBeatBest(score > 0);
      }
      onGameOver?.(score);
    },
    [bestKey, onGameOver],
  );

  const { canvasRef, wrapRef, snapshot, restart, handlers } = useGame({
    ballCount,
    art,
    onGameOver: handleGameOver,
  });

  // Driven straight from the snapshot into the DOM rather than through state, so the
  // warning can pulse smoothly without re-rendering the tree.
  const danger = snapshot?.dangerProgress ?? 0;
  useEffect(() => {
    if (dangerRef.current) dangerRef.current.style.opacity = String(danger * 0.9);
  }, [danger]);

  const over = snapshot?.phase === 'over';

  return (
    <div className={styles.shell}>
      <div className={styles.hud}>
        <div className={styles.scoreBlock}>
          <span className={styles.scoreLabel}>Score</span>
          <span className={styles.score}>{(snapshot?.score ?? 0).toLocaleString()}</span>
        </div>
        <div className={styles.nextBlock}>
          <span className={styles.nextLabel}>Next</span>
          {snapshot ? <NextBall rank={snapshot.nextRank} count={ballCount} art={art} /> : null}
        </div>
      </div>

      <div className={styles.board} ref={wrapRef}>
        <div className={styles.stage}>
          <canvas
            ref={canvasRef}
            className={styles.canvas}
            {...handlers}
            aria-label="Game board. Tap or click to drop a ball."
          />
          <div className={styles.danger} ref={dangerRef} aria-hidden="true" />

          {over ? (
            <div className={styles.overlay}>
              <h2 className={styles.overTitle}>Board full!</h2>
              <p className={styles.finalScore}>{(snapshot?.score ?? 0).toLocaleString()}</p>
              {beatBest ? (
                <p className={styles.newBest}>★ New personal best</p>
              ) : (
                <p className={styles.best}>Best: {best.toLocaleString()}</p>
              )}
              {overlayExtra ? <div className={styles.overExtra}>{overlayExtra}</div> : null}
              <div className={styles.overActions}>
                {shareUrl ? (
                  <ShareScore
                    score={snapshot?.score ?? 0}
                    url={shareUrl}
                    gameTitle={shareTitle}
                  />
                ) : null}
                <Button
                  block
                  onClick={() => {
                    setBeatBest(false);
                    onRestart?.();
                    restart();
                  }}
                >
                  Play again
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {footer ?? (
        <p className={styles.hint}>
          Best {best.toLocaleString()} · merge two of the same to grow
        </p>
      )}
    </div>
  );
}
